import React, { useState } from "react";
import { Navigate } from "react-router-dom";

import { Pagination } from "@performyard/py-components";

import PageHeading from "../../../components/PageHeading";
import AllTodosTable from "./AllTodosTable";
import DownloadTodosCsvButton from "./DownloadTodosCsvButton";
import ReadableTodosFilterChips, { convertTodosFiltersToReadableList } from "./ReadableTodosFilterChips";
import TodosFilterPanel, { TodosFilters } from "./TodosFilterPanel";

import { useWorkflowTodos } from "../../../api/hooks";
import { useIsNewNavigation } from "../../../api/hooks/defs/navigation";

import type { WorkflowTodoFilterParams } from "../../../api/services/defs/workflows";

import InfoPageTemplate from "../../../pageTemplates/InfoPageTemplate";
import StandardToolbarActionRow from "../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
import useInfoPageTemplateQueryState from "../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
import WorkflowsInfoPanel from "../WorkflowsInfoPanel";
import { WORKFLOWS_NAVIGATION_LINKS } from "../navigationLinks";
import {
	MAX_SEARCH_LENGTH,
	isNamedReference,
	isSerializedDate,
	sanitizePageNumber,
	sanitizeSortBy,
	sanitizeSortDirection,
	toDateOnlyParam,
} from "../todoFilterState";
import useWorkflowsPageGate from "../useWorkflowsPageGate";

const PAGE_TITLE = "Company To-Do's";
const PAGE_SIZE = 25;

const normalizeTodosFilters = (filters: Partial<TodosFilters>): Partial<TodosFilters> => {
	const normalized: Partial<TodosFilters> = {};
	for (const key of ["assignee", "workflow", "actor"] as const) {
		const value = filters[key];
		if (isNamedReference(value)) {
			normalized[key] = { id: value.id, name: value.name };
		}
	}
	if (filters.status === "active" || filters.status === "completed" || filters.status === "overdue") {
		normalized.status = filters.status;
	}
	for (const key of ["dueDateStart", "dueDateEnd", "completedDateStart", "completedDateEnd"] as const) {
		const value = filters[key];
		if (value instanceof Date || isSerializedDate(value)) {
			normalized[key] = value as Date;
		}
	}
	return normalized;
};

const AllTodos = () => {
	const isNewNav = useIsNewNavigation();
	const [isFiltersOpen, setIsFiltersOpen] = useState(false);

	const { isGateLoading, featureAvailable, canView } = useWorkflowsPageGate();

	const {
		pageNumber,
		setPageNumber,
		sort,
		setSort,
		filters,
		setFilters,
		searchValue,
		debouncedSearchValue,
		setDebouncedSearch,
	} = useInfoPageTemplateQueryState<TodosFilters>({
		defaultSortName: "due_date",
		defaultSortDirection: "asc",
		defaultPageNumber: 1,
		fallbackFilters: { status: "active" },
		normalizeFilters: normalizeTodosFilters,
	});
	const safePageNumber = sanitizePageNumber(pageNumber);

	const filterParams: WorkflowTodoFilterParams = {
		action: debouncedSearchValue?.slice(0, MAX_SEARCH_LENGTH) || undefined,
		subject: filters.assignee?.id,
		actor: filters.actor?.id,
		workflow: filters.workflow?.id,
		status: filters.status,
		dueDateStart: toDateOnlyParam(filters.dueDateStart),
		dueDateEnd: toDateOnlyParam(filters.dueDateEnd),
		completedDateStart: toDateOnlyParam(filters.completedDateStart),
		completedDateEnd: toDateOnlyParam(filters.completedDateEnd),
	};

	const { data, isLoading } = useWorkflowTodos(
		{
			...filterParams,
			sortBy: sanitizeSortBy(sort.name),
			sortOrder: sanitizeSortDirection(sort.direction),
			limit: PAGE_SIZE,
			skip: (safePageNumber - 1) * PAGE_SIZE,
		},
		{ enabled: !isGateLoading && featureAvailable && canView }
	);

	if (isGateLoading) {
		return null;
	}

	if (!featureAvailable) {
		return <Navigate to="/feature-disabled/" replace />;
	}

	if (!canView) {
		return <WorkflowsInfoPanel>Missing required permissions.</WorkflowsInfoPanel>;
	}

	const todos = data?.todos ?? [];
	const totalCount = data?.totalCount ?? 0;
	const pageCount = Math.ceil(totalCount / PAGE_SIZE);

	const activeFilterChips = convertTodosFiltersToReadableList(filters);

	const actionButtons = [<DownloadTodosCsvButton key="download-todos-csv" params={filterParams} />];

	const toolbarActionRow = (
		<StandardToolbarActionRow
			searchInput={searchValue}
			onSearchInputChange={setDebouncedSearch}
			onFilterButtonPress={() => setIsFiltersOpen(!isFiltersOpen)}
			activeFiltersCount={activeFilterChips.length}
			searchPlaceholder="Search To-Dos..."
		/>
	);

	const toolbarInformationRow = <ReadableTodosFilterChips filters={filters} setFilters={setFilters} />;

	const drawer = (
		<TodosFilterPanel
			isOpen={isFiltersOpen}
			onClose={() => setIsFiltersOpen(false)}
			filters={filters}
			onApplyFilters={(newFilters) => setFilters(newFilters)}
		/>
	);

	const mainContent = (
		<div>
			<AllTodosTable
				todos={todos}
				isLoading={isLoading}
				onSortChange={setSort}
				activeSort={sanitizeSortBy(sort.name)}
				activeSortDirection={sanitizeSortDirection(sort.direction)}
				emptyStateText="No To-Do’s Found"
			/>
			{pageCount > 1 && (
				<div data-testid="paginationContainer" className="mt-4 flex justify-center">
					<Pagination currentPage={safePageNumber} pageCount={pageCount} onPageChange={setPageNumber} />
				</div>
			)}
		</div>
	);

	return (
		<InfoPageTemplate
			actionButtons={actionButtons}
			toolbarActionRow={toolbarActionRow}
			toolbarInformationRow={toolbarInformationRow}
			drawer={drawer}
			mainContent={mainContent}
			pageTitle={PAGE_TITLE}
			pageHeading={isNewNav ? <PageHeading>{PAGE_TITLE}</PageHeading> : undefined}
			navigationLinks={WORKFLOWS_NAVIGATION_LINKS}
			activeNavigationLinkKey="all-todos"
		/>
	);
};

export default AllTodos;
